import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
      })
      .catch((err) => {
        // A network failure (offline PWA reload) keeps the cached session; only a server rejection ends it.
        if (err?.status) {
          clearSession();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await api.login({ email, password });
    // MFA-enabled accounts get a challenge instead of a session.
    if (result.mfaRequired) return result;
    persistSession(result.user, result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const completeSession = useCallback((result) => {
    persistSession(result.user, result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const verifyMfa = useCallback(
    async (mfaToken, code) => completeSession(await api.verifyMfa({ mfaToken, code })),
    [completeSession]
  );

  const loginWithMagicLink = useCallback(
    async (token) => completeSession(await api.verifyMagicLink({ token })),
    [completeSession]
  );

  // Sign out: revoke the server-side token (best effort), then always clear client state.
  const logout = useCallback(async ({ everywhere = false } = {}) => {
    try {
      await (everywhere ? api.signOutEverywhere() : api.signOut());
    } catch {
      // Token may already be expired/revoked; local state is cleared regardless.
    }
    clearSession();
    setUser(null);
  }, []);

  // Server says the session is expired/revoked: drop local state so route guards redirect to sign-in.
  useEffect(() => {
    api.onSessionExpired = () => {
      clearSession();
      setUser(null);
    };
    return () => {
      api.onSessionExpired = null;
    };
  }, []);

  const value = { user, loading, isAuthenticated: !!user, login, verifyMfa, loginWithMagicLink, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
